import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import { CurlSecretNote, CliCiNote } from '@/features/integrations/components/GuideNotes'
import {   CLI_INSTALL_GITHUB, CLI_INSTALL_GITLAB, CLI_GITLAB_ID_TOKENS, TOKEN_EXCHANGE_CLI } from '@/features/integrations/snippets'

// ── Module Publishing ─────────────────────────────────────────────────────────
export function ModulePublishingGuide({ org, token, onSwitchGuide }: { org: OrgItem | null; token: string | null; onSwitchGuide: (id: GuideId) => void }) {
  const [platform, setPlatform] = useState<Platform>('cli')
  const orgSlug = org?.slug ?? '<org-slug>'
  const tokenVal = token ?? '$TOKEN'

  const cliSnippet = `# Run from the repo root. ` + "`terramantle init`" + ` scaffolds the terramantle.hcl
# manifest the repo-aware commands read.

# What changed since each module's last tag, and the next semver for it
terramantle modules changed

# Publish one module at an explicit version
terramantle modules publish --module my-module --version 1.2.0 --provider aws

# Or let conventional commits pick the version for every changed module
terramantle modules publish --provider aws

# Package + hash + print what would upload, with no network and no tag
terramantle modules publish --all --provider aws --dry-run`

  const curlSnippet = `${TOKEN_EXCHANGE_CLI}

# Package your module (from the module root)
tar -czf module.tar.gz -C ./my-module .

# Publish version 1.2.0
curl -X PUT \\
  https://registry.terramantle.dev/v1/modules/${orgSlug}/my-module/aws/1.2.0 \\
  -u "terramantle-bot:${tokenVal}" \\
  -H "Content-Type: application/octet-stream" \\
  --data-binary @module.tar.gz`

  const githubSnippet = `name: publish-modules

on:
  push:
    branches: [main]

permissions:
  contents: write   # repo tag pushes the <module>@<version> release tags
  id-token: write   # lets terramantle use the runner's ambient OIDC token

jobs:
  publish:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0   # conventional-commit versioning needs the tag history

${CLI_INSTALL_GITHUB}

      - name: Publish changed modules
        run: terramantle modules publish --provider aws --ci

      - name: Tag the released versions
        run: terramantle repo tag`

  const gitlabSnippet = `stages:
  - publish

publish:
  stage: publish
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}
    GIT_DEPTH: 0   # conventional-commit versioning needs the tag history
  script:
    - apt-get update && apt-get install -y curl git
${CLI_INSTALL_GITLAB}
    - terramantle modules publish --provider aws --ci
    - terramantle repo tag
  only:
    - main`

  return (
    <div>
      <h3 style={s.guideTitle}>Publishing Modules</h3>
      <p style={s.para}>
        The <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-getting-started')}>Terramantle CLI</button>{' '}
        packages, versions and uploads a module in one step - deterministic <code style={s.code}>tar.gz</code>,
        a version computed from your conventional commits, and the upload. You handle tagging and pushing -
        Terramantle handles storage, scanning, and serving. New to packaging? See{' '}
        <button style={s.linkBtn} onClick={() => onSwitchGuide('module-packaging')}>Packaging Module</button> first.
      </p>

      <PlatformTabs value={platform} onChange={setPlatform} />
      {platform === 'cli'    && <ShellSnippet code={cliSnippet}    language="bash" />}
      {platform === 'curl'   && <ShellSnippet code={curlSnippet}   language="bash" />}
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}

      {platform === 'cli' && (
        <p style={{ ...s.para, marginTop: 10 }}>
          <code style={s.code}>--provider</code> is required - the manifest carries no provider field.
          A released module with no bump-worthy commits since its last tag is skipped, so the command is safe to
          re-run. See <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-commands')}>Commands</button> for
          the publishing and tagging surface in full.
        </p>
      )}
      {platform === 'curl' && <CurlSecretNote />}
      {(platform === 'github' || platform === 'gitlab') && (
        <>
          <CliCiNote onSwitchGuide={onSwitchGuide} />
          <p style={{ ...s.para, marginTop: 10 }}>
            <code style={s.code}>modules publish</code> uploads the artefact; <code style={s.code}>repo tag</code>{' '}
            stamps the <code style={s.code}>&lt;module&gt;@&lt;version&gt;</code> git tags, so the job also needs
            push rights on the repo. It never re-creates an existing tag.
          </p>
        </>
      )}

      <h3 style={s.subTitle}>API endpoint</h3>
      <p style={s.para}>
        Under the covers it is a single HTTP PUT. The path encodes the namespace, name, provider, and version:
      </p>
      <ShellSnippet
        code={`PUT /v1/modules/:namespace/:name/:provider/:version\nContent-Type: application/octet-stream\nBody: .tar.gz of the module root`}
        language="http"
      />

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li>The archive must be a <code style={s.code}>.tar.gz</code> of the module root - not a parent directory.</li>
        <li>Versions follow semver (e.g. <code style={s.code}>1.2.0</code>). A leading <code style={s.code}>v</code> is stripped automatically.</li>
        <li>Terramantle runs security and misconfiguration scans after each publish.</li>
        <li>The bot token needs write access to the target namespace.</li>
      </ul>
    </div>
  )
}
